import Form from "@saleor/components/Form";
import { useDropOffListQuery } from "@saleor/dropOff/queries";
import useNotifier from "@saleor/hooks/useNotifier";
import { findOrderEnumNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import GrabExpressConfirmationDialog from "@saleor/motherOrders/components/GrabExpressConfirmationDialog";
import GrabExpressOverrideDialog from "@saleor/motherOrders/components/GrabExpressOverrideDialog";
import OrderAddRemarksDialog from "@saleor/motherOrders/components/OrderAddRemarksDialog";
import OrderAssignRiderDialog from "@saleor/motherOrders/components/OrderAssignRiderDialog";
import OrderCancelDialog from "@saleor/motherOrders/components/OrderCancelDialog";
import OrderConfirmationDialog from "@saleor/motherOrders/components/OrderConfirmationDialog";
import RiderRefreshDialog from "@saleor/motherOrders/components/OrderDetailsPage/components/RiderRefreshDialog";
import {
  useBook2Go,
  useBookThirdPartyRider,
  useMarkAsCompletedMutation,
  useMarkAsShippedMutation,
  useOrderAssignRider,
  useOrderCancelGrabExpress,
  useOrderCancelMutation,
  useOrderCancelPandagoAuto,
  useOrderReturnToFcMutation,
  userOrderFulfillmentRemarks,
  useUpdateOrderFulfillmentRemarks,
  useUpdateOrderStatusWithOverride
} from "@saleor/motherOrders/mutations";
import {
  useRiderDetailsQuery,
  useSearchRider
} from "@saleor/motherOrders/queries";
import {
  MotherOrderList,
  MotherOrderListVariables
} from "@saleor/motherOrders/types/MotherOrderList";
import {
  OrderListUrlDialog,
  OrderListUrlQueryParams
} from "@saleor/motherOrders/urls";
import { OrderCourierEnum } from "@saleor/motherOrders/utils/enums";
import { LAST_RIDER_ASSIGNED_AT } from "@saleor/motherOrders/utils/handlers";
import StoreOrderOverrideDialog from "@saleor/storeOrders/components/StoreOrderOverrideDialog";
import {
  CompletedReasonsEnum,
  LockStatus,
  LogisticsStatusEnum,
  SmopOrderStatusEnum,
  SmopUserTypeCode,
  TemplateStatusEnum
} from "@saleor/types/globalTypes";
import { ApolloQueryResult } from "apollo-client";
import React, { useState } from "react";

interface FulfillmentPageDialogsProps {
  params: OrderListUrlQueryParams;
  refetch: (
    variables?: MotherOrderListVariables
  ) => Promise<ApolloQueryResult<MotherOrderList>>;
  openModal: (
    action: OrderListUrlDialog,
    newParams?: OrderListUrlQueryParams
  ) => void;
  closeModal: () => void;
  refetchUnassignedRiders?: boolean;
  setRefetchUnassignedRiders?: (boolean) => void;
}

export const FulfillmentPageDialogs: React.FC<FulfillmentPageDialogsProps> = props => {
  const {
    params,
    refetch,
    openModal,
    closeModal,
    refetchUnassignedRiders,
    setRefetchUnassignedRiders
  } = props;

  React.useEffect(() => {
    if (refetchUnassignedRiders) {
      setRefetchUnassignedRiders(false);
    }
  }, [refetchUnassignedRiders]);

  const notify = useNotifier();

  const [orderId, setOrderId] = useState("");
  const [serverError, setServerError] = useState({
    timeDelivered: null,
    timeDispatched: null
  });
  const [cooldownDialogOpen, setCooldownDialogOpen] = useState(false);

  const { data: dropOffList } = useDropOffListQuery({
    displayLoader: true,
    skip: !params?.mallId,
    variables: {
      filter: {
        mall: params?.mallId,
        status: LogisticsStatusEnum.ACTIVE,
        templateStatus: TemplateStatusEnum.ACTIVE
      },
      first: 100
    }
  });

  const {
    search: searchRider,
    loadMore: loadMoreRider,
    result: resultRider
  } = useSearchRider({
    skip: !params?.mallId,
    variables: {
      first: 100,
      malls: [params?.mallId],
      query: "",
      status: LockStatus.ACTIVE,
      userType: [SmopUserTypeCode.RIDER]
    }
  });

  const { data: riderData, loading: loadingRider } = useRiderDetailsQuery({
    skip: !params.orderId,
    variables: { getTimeout: true, id: params.orderId }
  });

  React.useEffect(() => {
    if (params.orderId) {
      setOrderId(params.orderId);
    }
  }, [params.orderId]);

  const [orderUpdateStatus] = useUpdateOrderStatusWithOverride({
    onCompleted: data => {
      if (data.updateOrderStatus.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `Order ${
            data.updateOrderStatus.order.orderCode
          } is now marked as ${findOrderEnumNames(
            data.updateOrderStatus.order.smopStatus
          )}.`
        });
      } else {
        if (data.updateOrderStatus.orderErrors[0].field === "timeDispatched") {
          setServerError({
            ...serverError,
            timeDispatched: data.updateOrderStatus.orderErrors[0].message
          });
          openModal("onClickAssignRider", { orderId });
        } else {
          notify({
            status: "error",
            text: `${data.updateOrderStatus.orderErrors[0].message}`
          });
        }
      }
      refetch();
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  const [bookThirdPartyRider, bookingResult] = useBookThirdPartyRider({
    onCompleted: data => {
      const orderErrors = data.bookThirdPartyRider.orderErrors;
      const hasCooldownError = orderErrors.some(
        error => error.field === LAST_RIDER_ASSIGNED_AT
      );

      const courier =
        data?.bookThirdPartyRider?.order?.thirdPartyRider?.deliveryCourier
          ?.order?.courier;
      const referenceNo =
        data?.bookThirdPartyRider?.order?.thirdPartyRider?.deliveryCourier
          ?.referenceNo;
      const onBookingCourier =
        courier === OrderCourierEnum.GRAB_EXPRESS
          ? "Grab Express ID"
          : courier === OrderCourierEnum.PANDAGO_AUTO
          ? "Pandago Auto ID"
          : "";

      if (hasCooldownError) {
        setCooldownDialogOpen(true);
        return;
      } else if (orderErrors?.length === 0) {
        notify({
          status: "success",
          text: `You have successfully booked with ${onBookingCourier} ${referenceNo}`
        });
        closeModal();
      } else {
        notify({ status: "error", text: `${orderErrors[0]?.message}` });
      }
      setRefetchUnassignedRiders(true);
      refetch();
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      notify({ status: "error", text: error?.message });
    }
  });

  const [assignRider] = useOrderAssignRider({
    onCompleted: data => {
      const specifiedErrors = data.orderAssignRider.orderErrors;
      const hasCooldownError = specifiedErrors.some(
        error => error.field === LAST_RIDER_ASSIGNED_AT
      );
      if (hasCooldownError) {
        setCooldownDialogOpen(true);
        return;
      } else if (specifiedErrors.length === 0) {
        if (
          data.orderAssignRider.order.thirdPartyRider?.deliveryCourier ===
            null ||
          data.orderAssignRider.order?.thirdPartyRider === null
        ) {
          notify({
            status: "success",
            text: `Order ${data.orderAssignRider.order.orderCode} has been assigned a rider`
          });
        } else {
          const courier =
            data?.orderAssignRider?.order?.thirdPartyRider?.deliveryCourier
              ?.order?.courier;
          const referenceNo =
            data?.orderAssignRider?.order?.thirdPartyRider?.deliveryCourier
              ?.referenceNo;
          const onAssigningCourier =
            courier === OrderCourierEnum.GRAB_EXPRESS
              ? "Grab Express ID"
              : courier === OrderCourierEnum.PANDAGO_AUTO
              ? "Pandago Auto ID"
              : "";
          notify({
            status: "success",
            text: `You have successfully updated booking ${onAssigningCourier} ${referenceNo}`
          });
        }
      } else {
        notify({
          status: "error",
          text: `${data.orderAssignRider.orderErrors[0].message}`
        });
      }
      setRefetchUnassignedRiders(true);
      refetch();
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  const [manualRebookThirdPartyRider] = useBook2Go({
    onCompleted: ({ book2go }) => {
      if (book2go?.orderErrors?.length === 0) {
        notify({
          status: "success",
          text: `You have successfully rebooked with airway bill of ${book2go.order.thirdPartyAwbCode}`
        });
        closeModal();
      } else {
        notify({
          status: "error",
          text: `${book2go?.orderErrors[0]?.message}`
        });
      }
      refetch();
    }
  });

  const [orderCancelGrabExpress] = useOrderCancelGrabExpress({
    onCompleted: data => {
      if (data.orderCancelGrabExpress.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `You have successfully cancelled this booking.`
        });
      } else {
        notify({
          status: "error",
          text: `Unable to cancel. Please try again.`
        });
      }
    }
  });

  const [orderCancelPandagoAuto] = useOrderCancelPandagoAuto({
    onCompleted: data => {
      if (data?.orderCancelPandagoAuto?.orderErrors?.length === 0) {
        notify({
          status: "success",
          text: `You have successfully cancelled this booking.`
        });
      } else {
        notify({
          status: "error",
          text: `Unable to cancel. Please try again.`
        });
      }
    }
  });

  const [updateOrderCompleted] = useMarkAsCompletedMutation({
    onCompleted: data => {
      if (data.orderMarkAsCompleted.errors.length === 0) {
        notify({
          status: "success",
          text: `Order ${
            data.orderMarkAsCompleted.order.orderCode
          } is now marked as ${findOrderEnumNames(
            data.orderMarkAsCompleted.order.smopStatus
          )}.`
        });
      } else {
        if (data.orderMarkAsCompleted.errors[0].field === "timeDelivered") {
          setServerError({
            ...serverError,
            timeDelivered: data.orderMarkAsCompleted.errors[0].message
          });
          openModal("onClickAssignRider", { orderId });
        } else {
          notify({
            status: "error",
            text: `${data.orderMarkAsCompleted.errors[0].message}`
          });
        }
      }
      refetch();
    }
  });

  const [returnToFc] = useOrderReturnToFcMutation({
    onCompleted: e => {
      if (e.orderReturnToFc.errors.length === 0) {
        notify({
          status: "success",
          text: `Order ${
            e.orderReturnToFc.order.orderCode
          } is now marked as ${findOrderEnumNames(
            e.orderReturnToFc.order.smopStatus
          )}.`
        });
      } else {
        notify({
          status: "error",
          text: e.orderReturnToFc.errors[0].message
        });
      }
      refetch();
    }
  });

  const [orderMarkAsShipped] = useMarkAsShippedMutation({
    onCompleted: data => {
      if (data.orderMarkAsShipped.errors.length === 0) {
        notify({
          status: "success",
          text: `Order ${data.orderMarkAsShipped.order.orderCode} is now marked as shipped.`
        });
      } else {
        if (data.orderMarkAsShipped.errors[0].field === "timeDispatched") {
          setServerError({
            ...serverError,
            timeDispatched: data.orderMarkAsShipped.errors[0].message
          });
        } else {
          notify({
            status: "error",
            text: data.orderMarkAsShipped.errors[0].message
          });
        }
      }
      refetch();
    }
  });

  const [updateOrderCancelled] = useOrderCancelMutation({
    onCompleted: data => {
      if (data.orderCancel.errors.length === 0) {
        notify({
          status: "success",
          text: `Order ${
            data.orderCancel.order.orderCode
          } is now marked as ${findOrderEnumNames(
            data.orderCancel.order.smopStatus
          )}.`
        });
      } else {
        notify({
          status: "error",
          text: `${data.orderCancel.errors[0].message}`
        });
      }
      refetch();
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  const [updateRemark] = useUpdateOrderFulfillmentRemarks({
    onCompleted: data => {
      if (data.updateOrderFulfillmentRemarks.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `Remarks has been updated`
        });
      } else {
        notify({
          status: "error",
          text: `${data.updateOrderFulfillmentRemarks.orderErrors[0].message}`
        });
      }
      refetch();
    }
  });

  const [addRemark] = userOrderFulfillmentRemarks({
    onCompleted: data => {
      if (data.orderFulfillmentRemarks.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `Remarks has been saved`
        });
      } else {
        notify({
          status: "error",
          text: `${data.orderFulfillmentRemarks.orderErrors[0].message}`
        });
      }
      refetch();
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  return (
    <>
      <RiderRefreshDialog
        orderId={orderId}
        open={cooldownDialogOpen}
        onClose={() => {
          setCooldownDialogOpen(false);
          refetch();
        }}
      />
      <StoreOrderOverrideDialog
        open={params.action === "onOverrideStatus"}
        onClose={closeModal}
        orderStatus={params.orderStatus}
        newOrderStatus={params.newOrderStatus}
        onSubmitOverride={overrideReason =>
          orderUpdateStatus({
            variables: {
              id: params.orderId,
              overrideReason,
              smopStatus: params.newOrderStatus
            }
          })
        }
      />

      <GrabExpressOverrideDialog
        open={params.action === "onGrabOverrideStatus"}
        onClose={closeModal}
        onSubmitOverride={overrideReason => {
          if (params.newOrderStatus === SmopOrderStatusEnum.COMPLETED) {
            setOrderId(params.orderId);

            updateOrderCompleted({
              variables: {
                id: params.orderId,
                reason: CompletedReasonsEnum.DELIVERED
              }
            });
          } else {
            setOrderId(params.orderId);
            orderUpdateStatus({
              variables: {
                id: params.orderId,
                overrideReason,
                smopStatus: params.newOrderStatus
              }
            });
          }
        }}
      />

      <GrabExpressConfirmationDialog
        open={params.action === "confirmGrabOverride"}
        onClose={closeModal}
        onConfirm={() => {
          openModal("onGrabOverrideStatus");
        }}
      />

      <OrderConfirmationDialog
        open={params.action === "onClickSaveButton"}
        onClose={closeModal}
        motherOrderStatus={params.orderStatus}
        newOrderStatus={params.newOrderStatus}
        type={"ORDER_LIST"}
        handleSubmit={() => {
          if (params.newOrderStatus === SmopOrderStatusEnum.COMPLETED) {
            updateOrderCompleted({
              variables: {
                id: params.orderId,
                reason: CompletedReasonsEnum.DELIVERED
              }
            });
          } else if (
            params.newOrderStatus === SmopOrderStatusEnum.RETURN_TO_FC
          ) {
            returnToFc({
              variables: {
                id: params.orderId
              }
            });
          } else if (
            params.status === SmopOrderStatusEnum.RETURN_TO_FC &&
            params.newOrderStatus === SmopOrderStatusEnum.SHIPPED
          ) {
            orderMarkAsShipped({
              variables: {
                id: params.orderId
              }
            });
          } else {
            orderUpdateStatus({
              variables: {
                id: params.orderId,
                smopStatus: params.newOrderStatus
              }
            });
          }
          setOrderId(params.orderId);
        }}
      />

      <OrderAssignRiderDialog
        open={params.action === "onClickAssignRider"}
        onClose={closeModal}
        onRefetch={refetch}
        serverError={serverError}
        setServerError={setServerError}
        dropOffLocations={dropOffList?.dropOffLocations?.edges.map(
          e => e?.node
        )}
        thirdPartyBookingStatus={bookingResult.loading}
        riderList={maybe(() =>
          resultRider.data.search.edges
            .map(edge => edge.node)
            .filter(suggestedRider => suggestedRider.id)
        )}
        rider={maybe(() => riderData)}
        fetchRider={searchRider}
        fetchMoreRider={{
          hasMore: maybe(() => resultRider.data.search.pageInfo.hasNextPage),
          loading: resultRider.loading,
          onFetchMore: loadMoreRider
        }}
        loading={resultRider.loading || loadingRider}
        onSubmit={riderVariable =>
          assignRider({
            variables: {
              id: params.orderId,
              ...riderVariable
            }
          })
        }
        on3plBooking={riderVariable =>
          bookThirdPartyRider({
            variables: {
              id: params.orderId,
              ...riderVariable
            }
          })
        }
        onManualRebooking={riderVariable =>
          manualRebookThirdPartyRider({
            variables: {
              id: params.orderId,
              input: {
                packageHeight: riderVariable.height,
                packageLength: riderVariable.length,
                packageWeight: riderVariable.weight,
                packageWidth: riderVariable.width
              }
            }
          })
        }
        onGrabExpressCancel={() => {
          orderCancelGrabExpress({
            variables: {
              id: params.orderId
            }
          });
        }}
        onPandagoAutoCancel={(cancelReason: string) => {
          orderCancelPandagoAuto({
            variables: {
              cancelReason,
              id: params.orderId
            }
          });
        }}
      />

      <Form
        onSubmit={null}
        initial={{
          cancelInput: "",
          cancelReason: "",
          cancelType: ""
        }}
      >
        {({ change, data }) => (
          <OrderCancelDialog
            open={params.action === "cancel"}
            onClose={closeModal}
            handleSubmit={event =>
              updateOrderCancelled({
                variables: {
                  cancelReason: event.cancelReason,
                  id: params.orderId
                }
              })
            }
            onChange={change}
            data={data}
            name="cancelType"
            isOrderGrab={params.courier === OrderCourierEnum.GRAB_EXPRESS}
          />
        )}
      </Form>

      <OrderAddRemarksDialog
        open={params.action === "onClickAddRemarks"}
        onClose={closeModal}
        oldRemarks={params.orderOldRemarks}
        onSubmitRemark={remarkVariable => {
          if (
            params.action === "onClickAddRemarks" &&
            params.orderOldRemarkId
          ) {
            updateRemark({
              variables: {
                id: params.orderId,
                remarks: remarkVariable
              }
            });
          } else {
            addRemark({
              variables: {
                id: params.orderId,
                remarks: remarkVariable
              }
            });
          }
        }}
      />
    </>
  );
};

export default FulfillmentPageDialogs;
